import type { ColumnDef } from "@tanstack/react-table";
import type { SortDescriptor } from "react-stately";

import { Link } from "@tanstack/react-router";

import { DraggableTable as Table } from "@/components/table";
import { useTableSorting } from "@/hooks/useTableSorting";

import type { AiCostsRow } from "./hooks/useAiCosts";

import { AI_COSTS_DEFAULT_SORT } from "./aiCostsSearch";
import { formatMicroUsd } from "./formatUsd";

const AI_COSTS_PAGE_SIZE = 10;

const formatCount = (value: number) => value.toLocaleString("en-US");

function numberColumn(
  accessorKey: keyof AiCostsRow,
  header: string,
  format: (value: number) => string = formatCount,
): ColumnDef<AiCostsRow> {
  return {
    accessorKey,
    cell: ({ getValue }) => format(getValue() as number),
    header,
    id: accessorKey,
    size: 120,
  };
}

function nameColumn(header: string): ColumnDef<AiCostsRow> {
  return {
    accessorKey: "name",
    cell: ({ row }) =>
      row.original.href ? (
        <Link
          className="font-medium text-text-primary hover:text-text-brand-secondary"
          to={row.original.href}
        >
          {row.original.name}
        </Link>
      ) : (
        <span className="font-medium text-text-primary">
          {row.original.name}
        </span>
      ),
    header,
    id: "name",
    size: 240,
  };
}

const COST_COLUMNS: ColumnDef<AiCostsRow>[] = [
  numberColumn("costMicroUsd", "Cost", formatMicroUsd),
  numberColumn("events", "Events"),
  numberColumn("failedEvents", "Failed"),
  numberColumn("inputTokens", "Input tokens"),
  numberColumn("cachedInputTokens", "Cached tokens"),
  numberColumn("outputTokens", "Output tokens"),
  numberColumn("images", "Images"),
  numberColumn("megapixels", "Megapixels"),
];

const SORTABLE_COLUMNS = [
  "name",
  ...COST_COLUMNS.map((column) => column.id as string),
];

interface AiCostsTableProps {
  isLoading: boolean;
  nameHeader: string;
  onPageChange: (page: number) => void;
  onSortChange: (sortDescriptor: SortDescriptor) => void;
  page: number;
  rows: AiCostsRow[];
  sortDescriptor: SortDescriptor;
}

export function AiCostsTable({
  isLoading,
  nameHeader,
  onPageChange,
  onSortChange,
  page,
  rows,
  sortDescriptor,
}: AiCostsTableProps) {
  const { handleSort, sorting } = useTableSorting({
    defaultColumn: AI_COSTS_DEFAULT_SORT,
    defaultDirection: "descending",
    setSortDescriptor: onSortChange,
    sortDescriptor,
  });

  const sortedRows = sortRows(rows, sortDescriptor);
  const totalPages = Math.max(1, Math.ceil(rows.length / AI_COSTS_PAGE_SIZE));

  return (
    <div className="rounded-b-xl bg-bg-primary overflow-auto">
      <Table
        className="rounded-b-xl w-full"
        columns={[nameColumn(nameHeader), ...COST_COLUMNS]}
        data={sortedRows.slice(
          (page - 1) * AI_COSTS_PAGE_SIZE,
          page * AI_COSTS_PAGE_SIZE,
        )}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        isServerSideSorting
        onSort={handleSort}
        pagination={{
          metadata: {
            currentPage: page,
            itemsPerPage: AI_COSTS_PAGE_SIZE,
            totalItems: rows.length,
            totalPages,
          },
          onPageChange,
        }}
        sortableColumns={SORTABLE_COLUMNS}
        sorting={sorting}
      />
    </div>
  );
}

function sortRows(
  rows: AiCostsRow[],
  { column, direction }: SortDescriptor,
): AiCostsRow[] {
  const key = column as keyof AiCostsRow;
  const sign = direction === "ascending" ? 1 : -1;

  return rows.toSorted((a, b) => {
    const left = a[key] ?? "";
    const right = b[key] ?? "";

    return (
      sign *
      (typeof left === "number" && typeof right === "number"
        ? left - right
        : String(left).localeCompare(String(right)))
    );
  });
}
